import {useState} from "react"

// Context
import {ThreadDrawerProvider} from "@/features/thread-drawer"

// Components
import {
  AlertsHeader,
  AlertsStats,
  AlertsList,
  FeatureUnavailableMessage,
  GroupedAlertsList,
} from "@/features/alerts"
import SetAlertsDrawerMenu from "@/features/alerts/components/AlertSettingsMenu"
import {ThreadDrawer} from "@/features/thread-drawer"

// Hooks
import {useAlertsWithStats} from "@/utils/hooks/useAlerts"
import {useDisclosure} from "@heroui/react"

function Alerts() {
  const [showAlerts, setShowAlerts] = useState<boolean>(true)
  const [viewMode, setViewMode] = useState<"grouped" | "individual">(
    "individual"
  )
  const {
    isOpen: isSetAlertsDrawerOpen,
    onOpen: onSetAlertsDrawerOpen,
    onOpenChange: onSetAlertsDrawerOpenChange,
  } = useDisclosure()

  // Use combined alerts and stats hook - no callbacks needed!
  const {
    // Alerts data
    loading: alertsLoading,
    error: alertsError,
    data: allAlerts,
    unresolvedAlerts,
    resolvedAlerts,
    updatingItems,
    setItemUpdating,
    updateAlertOptimistically,
    moveAlertBetweenLists,
    refetchInBackground,
    // Stats data
    statsLoading,
    statsError,
    stats,
  } = useAlertsWithStats()

  return (
    <ThreadDrawerProvider>
      <div className="animate-fade animate-once animate-ease-linear mr-4 flex w-full justify-between gap-4 rounded-md border-black/5 bg-onyx-white p-4 transition-all duration-150">
        {/* Main alerts panel - matches archive page structure */}
        <div className="flex h-full w-full min-w-[30%] flex-col gap-2 overflow-auto rounded-md bg-white">
          <div className="p-4">
            {/* Header with controls */}
            <AlertsHeader
              showAlerts={showAlerts}
              onAlertControlClick={onSetAlertsDrawerOpen}
              title="Intelligent Alerts"
              viewMode={viewMode}
              onViewModeChange={setViewMode}
            />

            {/* Show error if data failed to load */}
            {alertsError && (
              <div className="rounded-md border border-red-200 bg-red-50 p-4">
                <div className="text-sm text-red-800">
                  <strong>Error loading alerts:</strong> {alertsError}
                </div>
              </div>
            )}

            {/* Feature unavailable message (only shown when feature is disabled) */}
            {!showAlerts && (
              <FeatureUnavailableMessage
                title="Alerts"
                description="Sorry, alerts are not available on a trial plan. Please upgrade to a paid plan to enable this feature."
              />
            )}

            {/* Stats cards */}
            <AlertsStats
              loading={statsLoading}
              error={statsError}
              stats={stats}
            />
          </div>

          {/* Scrollable alerts list */}
          <div className="flex-1 overflow-auto">
            {viewMode === "grouped" ? (
              <GroupedAlertsList
                unresolvedAlerts={unresolvedAlerts}
                resolvedAlerts={resolvedAlerts}
                loading={alertsLoading}
                updatingItems={updatingItems}
                optimisticUpdateFunctions={{
                  setItemUpdating,
                  updateAlertOptimistically,
                  moveAlertBetweenLists,
                  refetchInBackground,
                }}
              />
            ) : (
              <AlertsList
                unresolvedAlerts={unresolvedAlerts}
                resolvedAlerts={resolvedAlerts}
                loading={alertsLoading}
                updatingItems={updatingItems}
                optimisticUpdateFunctions={{
                  setItemUpdating,
                  updateAlertOptimistically,
                  moveAlertBetweenLists,
                  refetchInBackground,
                }}
              />
            )}
          </div>

          {/* Drawer menu for settings */}
          <SetAlertsDrawerMenu
            isOpen={isSetAlertsDrawerOpen}
            onOpenChange={onSetAlertsDrawerOpenChange}
          />
        </div>

        {/* Thread drawer - positioned similar to EmailView in archive */}
        <ThreadDrawer />
      </div>
    </ThreadDrawerProvider>
  )
}

export default Alerts
